export type ResizeHandle =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

export const handleAffectsWidth = (handle: ResizeHandle): boolean =>
  handle === "left" || handle === "right" || handle.includes("-");

export const handleAffectsHeight = (handle: ResizeHandle): boolean =>
  handle === "top" || handle === "bottom" || handle.includes("-");

export const isLeftSideHandle = (handle: ResizeHandle): boolean =>
  handle === "left" || handle === "top-left" || handle === "bottom-left";

export const isRightSideHandle = (handle: ResizeHandle): boolean =>
  handle === "right" || handle === "top-right" || handle === "bottom-right";

export const isTopSideHandle = (handle: ResizeHandle): boolean =>
  handle === "top" || handle === "top-left" || handle === "top-right";

export const isBottomSideHandle = (handle: ResizeHandle): boolean =>
  handle === "bottom" || handle === "bottom-left" || handle === "bottom-right";

export const getWidthDelta = (handle: ResizeHandle, delta: { x: number; y: number }): number =>
  isLeftSideHandle(handle) ? -delta.x : delta.x;

export const getHeightDelta = (handle: ResizeHandle, delta: { x: number; y: number }): number =>
  isTopSideHandle(handle) ? -delta.y : delta.y;

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface ApplyResizeDeltaParams {
  rect: Rect;
  handle: ResizeHandle;
  delta: { x: number; y: number };
  minSize?: number;
}

/**
 * Apply a screen-space drag delta to a rect for a given resize handle. Returns
 * a new rect where the moved edges follow the handle. Width/height are clamped
 * to `minSize` (default 1px). `top`/`left` shift only when a top/left-side
 * handle is dragged.
 *
 * The delta is expected to be in the same coordinate system as the rect.
 */
export const applyResizeDelta = ({ rect, handle, delta, minSize = 1 }: ApplyResizeDeltaParams): Rect => {
  let { top, left, width, height } = rect;

  if (handleAffectsWidth(handle)) {
    const widthDelta = getWidthDelta(handle, delta);
    width = Math.max(minSize, width + widthDelta);
    if (isLeftSideHandle(handle)) {
      // Clamp left to keep it consistent with width: when the drag would push
      // width below minSize, left stops at right - minSize rather than
      // overshooting through the right edge.
      left = rect.left + rect.width - width;
    }
  }

  if (handleAffectsHeight(handle)) {
    const heightDelta = getHeightDelta(handle, delta);
    height = Math.max(minSize, height + heightDelta);
    if (isTopSideHandle(handle)) {
      top = rect.top + rect.height - height;
    }
  }

  return { top, left, width, height };
};
